<template>
    <div v-if="(config.display)" :style="{'width': config.width || '100%'}" class="control e-control">
        <label-wrapper :label-settings="config"></label-wrapper>
        <div v-if="asField">
            <display-value :id="config.id" :value="value"></display-value>
        </div>
        <div v-else>
            <div v-if="!optionsReady" class="loading-message">
                Getting data. Please wait...
            </div>
            <div v-else>
                <div v-if="Array.isArray(value) && optionsReady" :class="{'is-dragging': config.allowDragAndDrop && dragIndex !== false }">
                    <div v-for="(iAction, index) in value" :key="index"
                         class="object-item-header"
                         :class="{'dragged-item': dragIndex === index, 'selected-action': selectedIndex === index}"
                         :draggable="config.allowDragAndDrop && dragIndex === index"
                         @dragstart="onDragstart($event, iAction, index)"
                         @dragend="onDragend($event, iAction, index)"
                    >
                        <div v-if="config.allowDragAndDrop" @mousedown="dragIndex = index;" @mouseup="dragIndex = false;" :id="'drag_handle_' + index" class="drag-drop-handle"><span class="e-drag-and-drop"><i class="fa-solid fa-grip-dots-vertical"></i></span></div>
                        <div :id="index + '_drop_zone'" class="drop-zone" :class="{'first-drop-zone': index === 0}" @drop="onDrop($event, iAction, index)" @dragover="onDragover($event, iAction)" @dragleave="onDragleave"><div class="drop-indicator">{{}}</div></div>
                        <div class="sub-list-action-item-container">
                            <div class="edit-action-field">
                                <ejs-dropdownlist
                                    v-model:value="iAction.action"
                                    :fields="config.fields"
                                    :dataSource='options'
                                    :readonly="config.readonly || config.disabled"
                                    :floatLabelType="config.floatLabelType || 'Never'"
                                    :placeholder="config.placeholder"
                                    v-on:click="action($event, 'onclick')"
                                    v-on:change="actionFieldChange($event, 'onchange', index)"
                                    v-on:filtering="action($event, 'onfiltering')"
                                    :ref="config.ref || config.id"
                                    :data-test-id="dataTestId + '_' + index"
                                    :enabled="!config.disabled"
                                    :showClearButton="showClearButton"
                                ></ejs-dropdownlist>
                                <div>
                                    <ejs-button v-on:click="openParameters(index)"
                                        :ref="(config.ref || config.id) + '_' + index + '_button'"
                                        type="button"
                                        title="Parameters"
                                        :disabled="config.disableEdit || config.disabled"
                                        :data-test-id="dataTestId + '_' + index + '_edit_button'"
                                        :cssClass="'e-link edit-params-btn'"
                                        :iconCss="'fa-light fa-sliders-simple'"
                                    ></ejs-button>
                                </div>
                                <ejs-button v-on:click="removeAction(index)"
                                    :ref="(config.ref || config.id) + '_' + index + '_remove_button'"
                                    type="button"
                                    title="Remove"
                                    :disabled="config.disableEdit || config.disabled"
                                    :data-test-id="dataTestId + '_' + index + '_remove_button'"
                                    :cssClass="'e-link delete-params-btn'"
                                    :iconCss="'e-icons e-delete-icon'"
                                ></ejs-button>
                            </div>
                            <div>
                                {{iAction?.params?.runNext}}<span v-if="iAction?.params?.runNext === 'sequential'"> | On Status: Success {{iAction?.params?.executeIfResponse}}</span>
                            </div>
                        </div>
                        <div v-if="index === value.length - 1" :id="index + '_drop_zone'" class="drop-zone last-drop-zone" @drop="onDrop($event, iAction, index)" @dragover="onDragover($event, iAction, true)" @dragleave="onDragleave"><div class="drop-indicator">{{}}</div></div>
                    </div>
                </div>
                <div class="btn-wrapper">
                    <ejs-button v-on:click="addAction()"
                                :ref="(config.ref || config.id) + '_add_button'"
                                type="button"
                                title="Add"
                                :disabled="config.disableEdit || config.disabled"
                                :data-test-id="dataTestId + '_add_button'"
                                :cssClass="'add-action-button'"
                                :width="'100%'"
                    >Add Action</ejs-button>
                    <control v-if="Array.isArray(value) && value.length"
                             style="display: inline-block; margin-top:2px;"
                             name="ENABLE_PRE_RUN_MESSAGE"
                             :prop-config="controlConfigs.ENABLE_PRE_RUN_MESSAGE"
                             :value="value[0].enablePreRunMessage"
                             @update:value="value[0].enablePreRunMessage = $event;"
                    ></control>
                    <ejs-button v-if="Array.isArray(value) && value.length" v-on:click="openMessageConfigDialog()"
                                :ref="(config.ref || config.id) + '_before_message'"
                                type="button"
                                title="view/edit message"
                                :disabled="config.disableEdit || config.disabled"
                                :data-test-id="dataTestId + '_before_message'"
                                :cssClass="'e-link before-message'"
                                :iconCss="'e-icons e-link-icon'"
                    ><span class="fa-light fa-message-lines"></span> view/edit</ejs-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'pinia';
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { library } from "@common/library/library";
import { dialogStore } from "@common/dialogStore";
import { controlMixins } from "./controlMixins.js";

const controlComponent = defineAsyncComponent(() => import('@common/control/control.vue'));

let editorSettings = {};

export const actionFieldComponent = {
    editorSettings: editorSettings,
    mixins: [controlMixins],
    data() {
        return {
            dragIndex: false,
            selectedIndex: -1,
            controlConfigs: {
                ENABLE_PRE_RUN_MESSAGE: {
                    label: 'Enable Pre-run message',
                    width: 'auto',
                    type: 'checkbox',
                    helpText: 'Display a dialog message prior to running the action, with Ok and Cancel buttons.'
                }
            }
        };
    },
    watch: {
        value(newVal) {
            // convert old value model to new value model
            // TODO: Remove this code after all configurations have been updated https://samaritan.atlassian.net/browse/DEVD-1202
            if (typeof newVal === 'object' && !Array.isArray(newVal)) {
                let chainedActions = [];
                if (newVal.params && newVal.params.actionChaining) {
                    chainedActions = library.cloneObj(newVal.params.actionChaining);
                    delete newVal.params.actionChaining;
                }
                let formattedVal = [newVal];
                formattedVal = formattedVal.concat(chainedActions);
                this.value = formattedVal;
            }
            if (Array.isArray(this.value)) {
                for (let i in this.value) {
                    if (this.changedActions[this.value[i].action]) {
                        this.value[i].action = this.changedActions[this.value[i].action];
                    }
                }
            }
        }
    },
    computed: {
        showClearButton() {
            return typeof this.config.showClearButton !== 'undefined' ? this.config.showClearButton : true;
        },
    },
    created() {
        this.changedActions = actionStore.getChangedActions();
    },
    methods: {
        onDragstart(event, item, index) {
            item.index = index;
            event.dataTransfer.setData("application/json", JSON.stringify(item));
            event.dataTransfer.effectAllowed = "move";
        },
        onDragend(_event, _item) {
            this.dragIndex = false;
        },
        onDragover(event, _item, _lastItem) {
            event.preventDefault();
            event.target.classList.add('hovered');
            event.dataTransfer.dropEffect = "move";
        },
        onDragleave(event) {
            event.preventDefault();
            event.target.classList.remove('hovered');
        },
        onDrop(event, _item, index) {
            event.preventDefault();
            event.target.classList.remove('hovered');
            let movedItem = event.dataTransfer.getData("application/json");
            if (library.isJson(movedItem)) movedItem = JSON.parse(movedItem);
            let moveFromIndex = movedItem.index;
            let isLast = false;
            if (Array.isArray(this.value) && index === this.value.length - 1) isLast = true;
            let moveToIndex = index < movedItem.index || isLast ? index : index - 1;
            let { enablePreRunMessage, preRunMessage } = this.value[0];
            this.value = library.moveArrayItem(this.value, moveFromIndex, moveToIndex);
            if (enablePreRunMessage) {
                this.value[0].enablePreRunMessage = enablePreRunMessage;
                this.value[0].preRunMessage = preRunMessage;
            }
            this.value = [...this.value];
            this.dragIndex = false;
        },
        actionFieldChange(_$event, method, index) {
            if (!_$event.isInteracted) return;
            if (typeof index !== 'undefined') {
                this.value[index].params = {};
            } else {
                this.value.params = {};
            }
            this.action(this.value, method);
        },
        async removeAction(index) {
            let response = await library.showDialog('Are you sure you want to remove this action and it\'s parameters?');
            let { enablePreRunMessage, preRunMessage } = this.value[0];
            if (response.status === 'success') {
                if (!Array.isArray(this.value)) {
                    this.value = [];
                    this.addAction();
                } // TODO Remove after adjusting legacy configs
                else this.value.splice(index, 1);
                if (this.value.length && enablePreRunMessage) {
                    this.value[0].enablePreRunMessage = enablePreRunMessage;
                    this.value[0].preRunMessage = preRunMessage;
                }
                // Put back in a default
                if (Array.isArray(this.value) && !this.value.length) {
                    this.addAction();
                }
                this.value = [...this.value];
            }
        },
        addAction() {
            if (!this.value) this.value = [];
            else if (!Array.isArray(this.value)) this.value = [this.value];
            this.value.push({
                action: null,
                params: {}
            });
            this.value = [...this.value];
        },
        openParameters(index) {
            let tmpAction = typeof index !== 'undefined' ? this.value[index].action : this.value.action;
            let selectedActionConfig = this.options.find((item) => item.value === tmpAction);
            if (!tmpAction || !selectedActionConfig) return library.showToast('Select an action first.', { timeout: 5 });
            this.selectedIndex = index;
            let title = 'Action ' + (index + 1) + ': ' + selectedActionConfig.text;
            let parent = this;
            let parentValue = typeof index !== 'undefined' ? this.value[index] : this.value;
            if (library.objIsEmpty(parentValue.params)) parentValue.params = {};
            let dataObj = {
                config: parent.config,
                controlConfigs: {},
                controls: {},
                value: {
                    action: tmpAction,
                    params: {},
                    preRunMessage: parentValue.preRunMessage,
                    enablePreRunMessage: parentValue.enablePreRunMessage
                }
            };
            let dialogComponentObj = {
                name: "actionParamsDialog_" + library.getUniqueID(),
                data() {
                    return dataObj;
                },
                computed: {
                    // Handle proper unmount of template components and controls
                    ...mapState(dialogStore, {
                        opened(store) {
                            return store.dialogs[this.$options.name] && store.dialogs[this.$options.name].opened;
                        }
                    }),
                    paramValues() {
                        return this.value.params;
                    }
                },
                components: {
                    control: controlComponent,
                    "ejs-dropdownbutton": defineAsyncComponent(() => import('@common/syncfusion/dropdownButton')),
                    "ejs-button": defineAsyncComponent(() => import('@common/syncfusion/button')),
                }
            };
            dialogComponentObj.template = '<div v-if="opened">Provide the parameters you want for this action.';
            let tmpParams = Object.values(library.cloneObj(selectedActionConfig.params));
            let showInDialogItems = [];
            for (let j in tmpParams) {
                if (tmpParams[j].enableShowInDialog) {
                    showInDialogItems.push({ label: tmpParams[j].text, value: tmpParams[j].value });
                }
            }
            if (showInDialogItems.length) {
                tmpParams.push({
                    text: 'Show in dialog',
                    type: 'CHECKBOX_LIST',
                    value: 'showInDialog',
                    items: showInDialogItems,
                    message: 'Note: fields that are both required & empty will always show up in the action dialog.',
                    helpText: 'Show this field even If this option is not required, or it already has a value.'
                });
            }
            // }
            tmpParams.push({
                text: 'Run Next Action',
                value: 'runNext',
                type: 'radio',
                inlineLabel: true,
                items: [{
                    label: 'Immediately',
                    value: 'parallel'
                },
                {
                    label: 'After this one completes',
                    value: 'sequentially',
                }
                ],
                disabled: selectedActionConfig.onlyRunsParallel,
                message: selectedActionConfig.onlyRunsParallel
                    ? 'Run Option disabled, because this action must always run the next action in parallel.'
                    : 'Choosing "After this one completes" To use this action\'s response in the next action.',
                default: 'parallel',
            });
            tmpParams.push({
                text: 'Run Next On Status',
                value: 'executeIfResponse',
                type: 'dropdown',
                fields: { text: 'text', value: 'value' },
                items: [
                    { text: 'Success', value: 'success' },
                    { text: 'Failure', value: 'failure' },
                    { text: 'Any', value: 'any' },
                ],
                logic: [{
                    criteriaRecord: 'component',
                    record: 'self',
                    prop: 'disabled',
                    valueType: 'criteria',
                    criteria: 'this.component.paramValues.runNext !== "sequentially"'
                }],
                default: 'success'
            });
            // Set the template HTML
            tmpParams.forEach((param) => {
                let paramControl = library.cloneObj(param);
                delete paramControl.value;
                paramControl.label = paramControl.text;
                // let paramControlName = this.config.id + '_param_' + param.value;
                let paramControlName = 'param_' + index + '_' + param.value;
                paramControl.name = paramControlName;
                paramControl.removeOnDestroy = true;
                dataObj.controlConfigs[paramControlName] = paramControl;
                // Set default
                if (parentValue.params[param.value]) {
                    dataObj.value.params[param.value] = parentValue.params[param.value];
                } else {
                    dataObj.value.params[param.value] = param.default;
                }
                if (paramControl.type === 'dropdown' && Array.isArray(dataObj.value.params[param.value])) dataObj.value.params[param.value] = dataObj.value.params[param.value][0] || null;
                // set up right default value in the controlConfigs
                if (dataObj.value.params[param.value]) dataObj.controlConfigs[paramControlName].default = dataObj.value.params[param.value];
                // eslint-disable-next-line max-len
                dialogComponentObj.template += '<control global-store-id="' + dialogComponentObj.name + '" name="' + paramControlName + '" :display="controlConfigs[\'' + paramControlName + '\'].display" :prop-config="controlConfigs[\'' + paramControlName + '\']" :value="value.params[\'' + param.value + '\']" @update:value="value.params[\'' + param.value + '\'] = $event;" ' + (typeof index !== 'undefined' ? ' index = ' + index + ' ' : '') + '></control>';
            });
            dialogComponentObj.template += '</div>';
            dialogComponentObj = library.createTemplateComponent(dialogComponentObj.name, dialogComponentObj);
            let dialogSuccessFn = () => {
                let params = dataObj.value.params;
                // Remove unused params
                if (['controlValue', 'toggleBoolean', 'numberOffset'].indexOf(params.type) !== -1) delete params.val;
                if (params.type !== 'numberOffset') delete params.offset;
                delete params.toggleBooleanText;
                if (typeof index !== 'undefined') {
                    this.value[index] = dataObj.value;
                } else {
                    this.value = dataObj.value;
                }
                this.value = [...this.value];
                this.$forceUpdate();
            };
            let funcPostClose = () => {
                this.selectedIndex = -1;
            };
            return library.showDialog(dialogComponentObj, dialogSuccessFn, false, { title: title, name: dialogComponentObj.name, doNotCloseOnOK: true, }, false, funcPostClose);
        },
        openMessageConfigDialog() {
            let preRunMessageValue = this.value[0]?.preRunMessage || '';
            let dataObj = {
                preRunMessageValue,
                controlConfigs: {
                    PRE_RUN_MESSAGE: {
                        type: 'text',
                        label: 'Pre-run Message',
                        name: 'PRE_RUN_MESSAGE',
                        message: 'Provide a message that will display before executing this action.'
                    }
                }
            };
            let messageConfigDialogObj = {
                data() {
                    return dataObj;
                },
                computed: {
                    // Handle proper unmount of template components and controls
                    ...mapState(dialogStore, {
                        opened(store) {
                            return store.dialogs[this.$options.name] && store.dialogs[this.$options.name].opened;
                        }
                    })
                },
                components: {
                    control: controlComponent,
                },
                name: 'messageConfigDialog',
                template: `<div v-if="opened"><control name="PRE_RUN_MESSAGE" :prop-config="controlConfigs.PRE_RUN_MESSAGE" :value="preRunMessageValue" @update:value="preRunMessageValue = $event"></control></div>`
            };
            messageConfigDialogObj = library.createTemplateComponent(messageConfigDialogObj.name, messageConfigDialogObj);
            let dialogSuccessFn = () => {
                this.value[0].preRunMessage = dataObj.preRunMessageValue;
                this.value = [...this.value];
                this.$forceUpdate();
            };
            return library.showDialog(messageConfigDialogObj, dialogSuccessFn, false, { title: 'Pre-run Message', name: messageConfigDialogObj.name });
        }
    },
    components: {
        "control": defineAsyncComponent(() => import('@common/control/control')),
        "ejs-button": defineAsyncComponent(() => import('@common/syncfusion/button')),
        "ejs-dropdownbutton": defineAsyncComponent(() => import('@common/syncfusion/dropdownButton')),
        'ejs-dropdownlist': defineAsyncComponent(() => import('@common/syncfusion/dropdownList')),
    },
};

export default actionFieldComponent;
</script>

<style lang="css" scoped>
.edit-action-field {
    display: flex;
}
.edit-action-field :deep(.e-input-group.e-control-wrapper) {
    flex-grow: 9;
    margin-bottom: 0;
    margin-right: 2px;
}
:deep(.e-delete-icon::before) {
    content: "\e94a" !important;
}
:deep(.e-delete-icon) {
    color: #707070;
}
:deep(.e-delete-icon:hover) {
    color: #d64113;
}
:deep(.params-button) {
    background: yellow;
    color: black;
    width: 40px;
}
.action-items-list-wrapper {
    border: 1px solid lightgrey;
    border-radius: 4px;
}
.action-item-container {
    border-bottom: 1px solid lightgrey;
    padding: 6px;
}
.action-item-container:last-child {
    border-bottom: 0 none;
}

:deep(.action-parameters legend),
:deep(.action-parameters .label-text) {
    font-size: 1.2em !important;
}
:deep(.add-action-button) {
    display: inline-block;
    margin-top: 2px;
    margin-right: 10px;
    /*width: 100%;*/
}
.remove-button {
    margin-bottom: 5px;
}
:deep(.e-btn.e-link.edit-params-btn, .e-btn.e-link.delete-params-btn) {
    border: 1px solid #e5e5e5;
    padding: 7px;
    border-radius: 5px;
}
:deep(.edit-params-btn .e-btn-icon) {
    font-size: 18px;
}
/* Drag & Drop Classes */
.object-item-header {
    display: flex;
}
.sub-list-action-item-container {
    flex-grow: 1;
}
.selected-action:before {
    content: '\003E';
    font-weight: bold;
    font-size: 20px;
    padding: 9px 2px 0 2px;
    color: #666;
}
.selected-action {
    background: #bddbf9;
    border-radius: 5px;
}
:deep(.drag-drop-handle) {
    width: 20px;
    height: auto;
    margin: 1px;
    opacity: 0.4;
    cursor: grab;
    position: relative;
    left: -3px;
    top: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    transition: opacity .3s ease-in-out;
}
:deep(.fa-grip-dots-vertical) {
    font-size: 1.2rem;
}
:deep(.object-item-header:hover .drag-drop-handle) {
    opacity: 1;
}
:deep(.object-item-header:hover .drag-drop-handle:hover) {
    opacity: 1;
}
:deep(.dragged-item .drag-drop-handle) {
    cursor: grabbing;
}
:deep(.object-item-header.dragged-item) {
    background: #eff9fc;
    border: 2px dashed #0397BD !important;
}
:deep(.drop-zone) {
    position: absolute;
    width: 100%;
    height: 12px;
    left: 0;
    top: -7px;
    display: none;
}
:deep(.drop-zone.first-drop-zone) {
    top: 0;
    bottom: 0;
}
:deep(.drop-zone.last-drop-zone) {
    top: unset;
    bottom: 0;
}
:deep(.drop-indicator) {
    height: 4px;
    background: #3495ad;
    position: relative;
    top:4px;
    z-index: 1000;
    border-radius: 0 2px 2px 0;
    display: none;
    pointer-events: none; /* used to prevent dragleave when over indicator */
}
:deep(.drop-zone.first-drop-zone .drop-indicator) {
    top: 0;
}
:deep(.drop-zone.last-drop-zone .drop-indicator) {
    top: 8px;
}
/* :hover pseudo class does not work when holding mouse click down */
:deep(.drop-zone.hovered .drop-indicator) {
    display: block;
}
:deep(.is-dragging .drop-zone) {
    display: block;
}
/* Prevent display of the drop-zone of the item being dragged, and it's next sibling's drop-zone */
:deep(.is-dragging .dragged-item .drop-zone),
:deep(.is-dragging .dragged-item + .object-item-header .drop-zone:not(.last-drop-zone)) {
    display: none;
}
:deep(.is-dragging .object-item-header) {
    overflow: inherit;
}
:deep(.object-item-headers.is-dragging) {
    position: relative !important;
    overflow: inherit;
    overflow-x: visible !important;
}
:deep(.btn-wrapper) {
    margin-top: 1rem;
}
</style>
